import { useCallback, useEffect, useRef, useState } from 'react';

export type McpMode = 'controlled' | 'uncontrolled';

/**
 * Effect function for complex state updates in uncontrolled mode.
 * Receives a `setState` helper that uses functional updates internally,
 * so it's always safe to call from async callbacks (setTimeout, promises, etc.).
 */
export type EffectFn<S> = (helpers: { setState: (update: Partial<S>) => void }) => void;

/**
 * Generic hook for MCP component state management.
 *
 * Enforces a strict binary:
 *   - **Controlled**: state comes from `externalState` (parent pushes via iframe).
 *     `act()` only broadcasts events. The parent will push new state in response.
 *   - **Uncontrolled**: state comes from internal `useState`.
 *     `act()` broadcasts AND applies state updates / runs effects.
 *
 * Mode is **locked** on the first render where `mode` is explicitly provided.
 * After that, it cannot change — a dev warning is logged if it tries.
 *
 * Prop updates in controlled mode are automatic: `externalState` comes from
 * `useIframeRenderData`, which reacts to `ui-lifecycle-iframe-render-data` messages.
 * When the dashboard pushes new props → externalState changes → state re-derives → re-render.
 *
 * @template S - The full state shape for this component
 * @template E - The event union type this component broadcasts
 */
export function useMcpState<S, E>({
  mode: incomingMode,
  externalState,
  defaultState,
  broadcast,
}: {
  /** 'controlled' | 'uncontrolled' | undefined (before render data arrives) */
  mode: McpMode | undefined;
  /** Full or partial state from parent. Undefined until render data arrives. */
  externalState: Partial<S> | undefined;
  /** Complete defaults — used to fill gaps in partial externalState. */
  defaultState: S;
  /** Called on every act(). Typically wraps sendNotify with intent format. */
  broadcast: (event: E) => void;
}) {
  // ─── Lock mode on first explicit value ───
  const lockedMode = useRef<McpMode | null>(null);

  if (lockedMode.current === null && incomingMode !== undefined) {
    lockedMode.current = incomingMode;
  }

  if (
    import.meta.env.DEV &&
    lockedMode.current !== null &&
    incomingMode !== undefined &&
    lockedMode.current !== incomingMode
  ) {
    console.warn(
      `[useMcpState] Mode was locked as "${lockedMode.current}" on initialization, ` +
        `but received "${incomingMode}". Mode cannot change after initialization. Ignoring.`
    );
  }

  const mode: McpMode = lockedMode.current ?? 'uncontrolled';

  // ─── Internal state (uncontrolled mode only) ───
  const [internalState, setInternalState] = useState<S>(defaultState);

  // Hydrate internal state from external data on first arrival.
  // Render data is async — it arrives after mount, not during initial render.
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (mode === 'uncontrolled' && !hasHydrated.current && externalState !== undefined) {
      hasHydrated.current = true;
      setInternalState((prev) => ({ ...prev, ...externalState }));
    }
  }, [mode, externalState]);

  // ─── Source of truth ───
  // Controlled: re-derives from externalState every render.
  //   When dashboard pushes new props → externalState changes → state updates → re-render.
  // Uncontrolled: internal state. externalState only used for initial hydration.
  const state: S = mode === 'controlled' ? { ...defaultState, ...externalState } : internalState;

  // ─── act: the binary enforcer ───
  // Three call signatures:
  //   act(event)                            → broadcast only
  //   act(event, { title })                 → broadcast + immediate state update
  //   act(event, ({ setState }) => { ... }) → broadcast + effect with state control
  //
  // In controlled mode: always broadcasts, never touches state.
  //   The parent will process the event and push new externalState.
  // In uncontrolled mode: broadcasts AND applies the update/effect.
  const act = useCallback(
    (event: E, updateOrEffect?: Partial<S> | EffectFn<S>) => {
      broadcast(event);

      if (mode !== 'uncontrolled' || updateOrEffect === undefined) return;

      if (typeof updateOrEffect === 'function') {
        updateOrEffect({
          setState: (u) => setInternalState((prev) => ({ ...prev, ...u })),
        });
      } else {
        setInternalState((prev) => ({ ...prev, ...updateOrEffect }));
      }
    },
    [mode, broadcast]
  );

  return { state, act, mode };
}
